import { ArrowDownTrayIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import { classNames } from "src/helpers/classNames";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";

const DocumentItem = ({ doc }) => {
  const { workspace_id } = useParams();
  const { group, page } = useUrlQueryParams();

  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [downloadCompleteId, setDownloadCompleteId] = useState(null);

  const handleDownload = async (doc) => {
    try {
      setDownloadingDocId(doc._id);
      setProgress(0);
      setDownloadCompleteId(null);
      const { data: res } = await apiRequest(
        "GET",
        `/portal/workspaces/:workspace_id/documents/:document_id/download`,
        { params: { workspace_id, document_id: doc?._id }, queries: { group, page } },
        {
          is_abort: false,
          responseType: "blob",
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setProgress(percentCompleted);
            }
          },
        },
      );

      const blobUrl = URL.createObjectURL(res);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = doc?.name || "downloaded_document";
      link.click();
      URL.revokeObjectURL(blobUrl);
      setDownloadCompleteId(doc._id);
      setTimeout(() => {
        setDownloadCompleteId(null);
      }, 500);
    } catch (err) {
      toast.error(err?.message || "Failed to download");
    } finally {
      setDownloadingDocId(null);
      setProgress(0);
    }
  };

  return (
    <div
      key={doc?._id}
      className="flex items-center justify-between rounded-lg border border-highlightColor bg-leftNavActiveColor p-4 shadow transition">
      <span className="text-md max-w-[70%] truncate font-semibold text-leftNavActiveTextColor">{doc?.name}</span>

      <button
        onClick={() => handleDownload(doc)}
        className={classNames(
          downloadingDocId === doc?._id ? "pointer-events-none" : "",
          "relative flex h-12 w-12 items-center justify-center rounded-full border border-highlightColor bg-transparent text-leftNavActiveTextColor transition hover:bg-highlightColor hover:text-white disabled:cursor-not-allowed disabled:opacity-50",
        )}
        title={`Download ${doc?.name}`}>
        {downloadingDocId === doc?._id && (
          <svg
            className="absolute inset-0 h-full w-full rotate-[-90deg]"
            viewBox="0 0 36 36">
            <circle
              className="text-leftNavActiveTextColor transition-all duration-200"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={100}
              strokeDashoffset={100 - progress}
              cx="18"
              cy="18"
              r="16"
            />
          </svg>
        )}
        {downloadingDocId === doc?._id && progress === 0 ? (
          <div
            style={{ animationDuration: "1.2s" }}
            className="absolute h-full w-full animate-spin rounded-full border-4 border-t-transparent text-leftNavActiveTextColor transition-all duration-300"
          />
        ) : downloadingDocId === doc?._id && progress > 0 ? (
          <span className="z-10 font-mono text-xs">{progress}%</span>
        ) : downloadCompleteId === doc?._id ? (
          <CheckCircleIcon className="z-10 h-6 w-6 text-leftNavActiveTextColor" />
        ) : (
          <ArrowDownTrayIcon className="z-10 h-6 w-6 text-leftNavActiveTextColor" />
        )}
      </button>
    </div>
  );
};

export default DocumentItem;
